import React, { useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import ContinueWatching from 'app/utils/models/ContinueWatching';
import Link from 'next/link';
import Conditional from '../../../../components/Conditional/Conditional';
import { isBrowser } from '../../../../utils/environment';
import { logPageview } from '../../../../utils/linkpulse';
import { trackCustomEvent } from '../../../../utils/statistics/clientStatistics';
import { expandListClick } from '../../../../utils/statistics/customEvents';
import AccessibilityLinks from '../../../AccessibilityLinks/AccessibilityLinks';
import AccessibilityText from './components/AccessibilityText/AccessibilityText';
import EditButtons from './components/EditButtons/EditButtons';
import ExpandCollapseButton from './components/ExpandCollapseButton/ExpandCollapseButton';
import {
    Heading,
    HeaderLink,
    RemoveItemsButton,
    RemoveItemsButtonTransition,
    StyledHeader,
    BumpingArrowIcon,
} from './Header.styles';

type Props = {
    id: string;
    regTestSpec: string;
    url: string;
    text: string;
    expanded: boolean;
    expandable: boolean;
    allItemsVisible: boolean;
    changeLayout: (id: string) => void;
    showEditButtons: boolean;
    markedAsDeleted: Array<ContinueWatching>;
    onEditDone: () => void;
    onEditModeActivated: () => void;
    showAccessibilityLinks: boolean;
};

const Header = (props: Props): JSX.Element => {
    const {
        id,
        regTestSpec,
        url,
        text,
        expanded,
        expandable,
        allItemsVisible,
        changeLayout,
        showEditButtons,
        markedAsDeleted,
        onEditDone,
        onEditModeActivated,
        showAccessibilityLinks,
    } = props;

    const accessibilityText = useRef(null);

    const [editModeActivated, toggleEditModeActivated] = useState(false);

    if (!text && !showAccessibilityLinks) {
        return null;
    }

    const logLinkpulse = () => {
        if (isBrowser()) {
            logPageview({
                title: document.title,
            });
        }
    };

    const toggleExpand = (e) => {
        e.preventDefault();
        changeLayout(id);
        logLinkpulse();

        const action = !expanded ? 'expand' : 'collapse';

        trackCustomEvent(expandListClick(id, action));
    };

    const toggleEditMode = (e) => {
        e.preventDefault();
        if (editModeActivated) {
            onEditDone();
        } else {
            onEditModeActivated();
        }
        toggleEditModeActivated(!editModeActivated);
    };

    return (
        <StyledHeader>
            <Heading
                data-rt="slidable-list-header"
                data-rt-spec={regTestSpec}
                noBottomPadding={showAccessibilityLinks}
                as="h2"
            >
                <Conditional condition={showAccessibilityLinks}>
                    <AccessibilityLinks isVisible />
                </Conditional>
                <Conditional condition={!showAccessibilityLinks}>
                    <Conditional condition={url}>
                        <Link href={url} passHref>
                            <HeaderLink>
                                {text}
                                <BumpingArrowIcon aria-hidden="true" />
                            </HeaderLink>
                        </Link>
                    </Conditional>
                    <Conditional condition={!url}>{text}</Conditional>
                </Conditional>
            </Heading>
            <Conditional condition={expandable && !allItemsVisible && !showEditButtons}>
                <ExpandCollapseButton expanded={expanded} toggleExpand={toggleExpand} text={text} />
            </Conditional>
            <Conditional condition={showEditButtons}>
                <RemoveItemsButton type="button" onClick={toggleEditMode}>
                    <TransitionGroup>
                        <CSSTransition
                            key={`edit-mode-${editModeActivated}`}
                            mountOnEnter
                            unmountOnExit
                            timeout={150}
                        >
                            <RemoveItemsButtonTransition>
                                <EditButtons
                                    markedAsDeleted={markedAsDeleted}
                                    text={text}
                                    editModeActivated={editModeActivated}
                                />
                            </RemoveItemsButtonTransition>
                        </CSSTransition>
                    </TransitionGroup>
                </RemoveItemsButton>
            </Conditional>
            <span ref={accessibilityText} tabIndex={-1}>
                <AccessibilityText expanded={expanded} editModeActivated={editModeActivated} />
            </span>
        </StyledHeader>
    );
};

export default Header;
